import { useEffect, useState } from "react";
import { Lock, Send, MessageCircle, CheckCircle2, Clock3 } from "lucide-react";
import { toast } from "sonner";
import { getUTM, buildWhatsAppUrl, trackEvent } from "@/lib/utm";
import { getSessionId } from "@/lib/tracker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tipos = [
  { v: "", l: "Selecciona el tipo de espacio..." },
  { v: "estacionamiento", l: "Estacionamiento" },
  { v: "industria", l: "Industria / Nave industrial" },
  { v: "escuela", l: "Escuela o universidad" },
  { v: "residencial", l: "Residencial — terraza o jardín" },
  { v: "deportivo", l: "Área deportiva / Parques" },
  { v: "comercial", l: "Comercial / Corporativo" },
  { v: "otro", l: "Otro" },
];

const ciudades = [
  "Monterrey / MTY",
  "Saltillo",
  "Torreón",
  "Chihuahua",
  "Tamaulipas",
  "Bajío",
  "CDMX",
  "Otra ciudad",
];

export default function QuoteForm() {
  const [form, setForm] = useState({
    nombre: "",
    telefono: "",
    email: "",
    tipo_espacio: "",
    ciudad: "",
    mensaje: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  // Listen for prefill from Niches section, Calculadora, etc.
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail || {};
      if (d.tipo_espacio || d.mensaje || d.ciudad) {
        setForm((prev) => ({
          ...prev,
          ...(d.tipo_espacio ? { tipo_espacio: d.tipo_espacio } : {}),
          ...(d.mensaje ? { mensaje: d.mensaje } : {}),
          ...(d.ciudad ? { ciudad: d.ciudad } : {}),
        }));
      }
    };
    window.addEventListener("fs_prefill", handler);
    window.addEventListener("vt:prefillQuote", handler);
    // Also check sessionStorage on mount
    try {
      const pre = sessionStorage.getItem("fs_prefill_tipo");
      if (pre) setForm((prev) => ({ ...prev, tipo_espacio: pre }));
    } catch {}
    return () => {
      window.removeEventListener("fs_prefill", handler);
      window.removeEventListener("vt:prefillQuote", handler);
    };
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || form.nombre.length < 2) return toast.error("Ingresa tu nombre.");
    if (!form.telefono || form.telefono.replace(/\D/g, "").length < 8) return toast.error("Ingresa un teléfono válido.");
    if (!form.tipo_espacio) return toast.error("Selecciona el tipo de espacio.");
    if (!form.ciudad) return toast.error("Selecciona tu ciudad.");

    setLoading(true);
    try {
      const utm = getUTM();
      const sessionId = getSessionId();
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          fuente: "cotizacion",
          pagina: window.location.pathname,
          referrer: utm.referrer || document.referrer || null,
          utm_source: utm.utm_source || null,
          utm_medium: utm.utm_medium || null,
          utm_campaign: utm.utm_campaign || null,
          utm_term: utm.utm_term || null,
          utm_content: utm.utm_content || null,
          session_id: sessionId,
        }),
      });
      if (!res.ok) throw new Error("Error al enviar");
      const data = await res.json();
      trackEvent("form_submit", { form_id: "cotizacion", tipo: form.tipo_espacio, ciudad: form.ciudad });
      setSuccess(data);
      toast.success("¡Gracias! Te contactaremos en menos de 24 horas.");
    } catch (err) {
      toast.error("Hubo un problema. Inténtalo de nuevo o escríbenos por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const waDirect = buildWhatsAppUrl(
    "5218116289267",
    "Hola Eclipse, prefiero hablar por WhatsApp para cotizar FlexShade."
  );

  return (
    <section id="cotizar" className="bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a] py-20 md:py-28 text-white">
      <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <span className="eyebrow bg-red-500/10 border-red-500/30" style={{color: '#fca5a5'}}>Cotización sin costo</span>
          <h2 className="mt-4 section-title text-4xl md:text-6xl font-black leading-[1]">
            Obtén tu diseño y cotización <span className="text-orange-500">sin costo</span>
          </h2>
          <p className="mt-4 text-white/70 text-lg max-w-xl">
            Un experto de Eclipse te contactará en menos de 24 horas con la solución FlexShade a medida para tu espacio.
          </p>

          <div className="mt-8 space-y-4">
            {[
              "20 años de experiencia instalando en México",
              "+10,000 techos instalados con garantía por escrito",
              "Materiales importados premium (Dickson, Serge Ferrari, Sioen)",
              "Instalación inmediata, no requiere permiso de construcción",
              "Contamos con inventario listo para instalar",
            ].map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                <p className="text-white/85">{b}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-2xl border border-white/10 bg-white/5">
            <p className="text-white/80 font-semibold">¿Prefieres resolverlo por WhatsApp?</p>
            <p className="text-white/50 text-sm mt-1">Escríbenos y un asesor te responde de inmediato.</p>
            <a
              href={waDirect}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "form_side" })}
              className="btn-wa mt-3 px-5 py-3 rounded-lg inline-flex items-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Chatear ahora con un asesor
            </a>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-2xl text-neutral-900">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-black mb-2">¡Gracias {form.nombre.split(" ")[0]}!</h3>
              <p className="text-neutral-600 mb-6">
                {success.message}
              </p>
              <a
                href={success.whatsapp_url || waDirect}
                target="_blank"
                rel="noreferrer"
                className="btn-wa px-5 py-3 rounded-lg inline-flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" /> Agilizar por WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" noValidate className="space-y-4">
              <h3 className="section-title text-2xl md:text-3xl font-black">Solicita tu cotización gratis</h3>
              <p className="text-sm text-neutral-500">Un asesor Eclipse te contactará hoy mismo.</p>
              <div>
                <label className="form-label">Nombre completo *</label>
                <input name="nombre" data-testid="input-nombre" value={form.nombre} onChange={onChange} placeholder="Tu nombre" className="form-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Teléfono *</label>
                  <input name="telefono" data-testid="input-telefono" value={form.telefono} onChange={onChange} placeholder="Ej: 81 1234 5678" className="form-input" inputMode="tel" />
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <input name="email" data-testid="input-email" type="email" value={form.email} onChange={onChange} placeholder="tucorreo@email.com" className="form-input" />
                </div>
              </div>
              <div>
                <label className="form-label">Tipo de espacio *</label>
                <select name="tipo_espacio" data-testid="input-tipo" value={form.tipo_espacio} onChange={onChange} className="form-input">
                  {tipos.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Ciudad *</label>
                <select name="ciudad" data-testid="input-ciudad" value={form.ciudad} onChange={onChange} className="form-input">
                  <option value="">Selecciona tu ciudad...</option>
                  {ciudades.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Mensaje o descripción (opcional)</label>
                <textarea name="mensaje" data-testid="input-mensaje" value={form.mensaje} onChange={onChange} placeholder="Describe tu espacio: dimensiones aproximadas, cantidad de cajones, horarios, etc." className="form-input min-h-[80px]" rows={3}></textarea>
              </div>
              <button type="submit" data-testid="submit-form" disabled={loading} className="btn-primary w-full py-4 rounded-xl inline-flex items-center justify-center gap-2 text-base md:text-lg disabled:opacity-60">
                <Send className="w-5 h-5" />
                {loading ? "Enviando..." : "Quiero mi cotización gratis"}
              </button>
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <Lock className="w-3.5 h-3.5" /> Tu información es privada. No compartimos tus datos.
              </div>
              <div className="flex items-center gap-2 text-xs text-green-600 font-semibold">
                <Clock3 className="w-3.5 h-3.5" /> Respondemos en menos de 24 horas
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
